
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  type LinkProps as RouterLinkProps,
} from "react-router-dom";
import type {
  ImgHTMLAttributes,
  MouseEvent as ReactMouseEvent,
} from "react";
import { useState, useRef, useEffect } from "react";

import "./Navbar.css";

const signInWith = (path: string) =>
  `/signin?callbackUrl=${encodeURIComponent(path)}`;

type AppLinkProps = Omit<RouterLinkProps, "to"> & {
  to?: RouterLinkProps["to"];
  href?: RouterLinkProps["to"];
};

function Link({ to, href, ...props }: AppLinkProps) {
  return <RouterLink to={to ?? href ?? "/"} {...props} />;
}

type ImageProps = ImgHTMLAttributes<HTMLImageElement> & { priority?: boolean };

function Image({ priority: _priority, ...props }: ImageProps) {
  return <img {...props} />;
}

export default function Navbar({ isSignedIn }: { isSignedIn: boolean }) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isActive = (path: string) => pathname === path;
  const navigate = useNavigate();

  const close = () => setMenuOpen(false);
  const handleClick = () => {
    navigate(isSignedIn ? "/dashboard" : signInWith("/dashboard"));
  };

  const handleMyOrdersClick = () => {
    navigate(
      isSignedIn ? "/dashboard/orders" : signInWith("/dashboard/orders"),
    );
  };

  const handleHowItWorks = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    close();
    if (pathname === "/") {
      e.preventDefault();
      document.getElementById("how-it-works")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        menuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [menuOpen]);
  return (
    <div className="av-wrape-2 max-w-[1440px] mx-auto w-full">
      {/* ───────────── DESKTOP NAVBAR (unchanged) ───────────── */}
      <div
        data-animation="default"
        data-collapse="medium"
        data-duration="400"
        data-easing="ease"
        data-easing2="ease"
        role="banner"
        className="navbar w-nav"
      >
        <div className="container-15 w-container">
          <div className="nav-below-container">
            <Link href="/" className="w-nav-brand inline-block">
              <Image
                src="/Images/logo1.svg"
                alt="Swift MVP"
                width={120}
                height={40}
                className="w-nav-brand"
                priority
              />
            </Link>
            <nav role="navigation" className="nav-menu w-nav-menu">
              <Link
                href="/"
                className={`nav-link w-nav-link font-plus-jakarta-display font-normal ${isActive("/") ? "w--current" : ""}`}
                style={{ maxWidth: "100%" }}
              >
                Websites
              </Link>
              <Link
                href="/#pricing"
                className="nav-link w-nav-link"
                style={{ maxWidth: "100%" }}
              >
                Pricing
              </Link>
              <Link
                href="/dashboard/packages"
                className="nav-link w-nav-link"
                style={{ maxWidth: "100%" }}
              >
                Programs
              </Link>
              <Link
                href="/#how-it-works"
                className="nav-link w-nav-link"
                style={{ maxWidth: "100%" }}
              >
                Service
              </Link>
              <Link
                href="/dashboard/orders"
                className="nav-link w-nav-link"
                style={{ maxWidth: "100%" }}
              >
                My orders
              </Link>
            </nav>
            <div
              className="menu-button-2 w-nav-button"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <div className="w-icon-nav-menu"></div>
            </div>
            <button
              onClick={handleClick}
              className="button-4 desktop w-button transition-all duration-300 hover:scale-[1.05] active:scale-[0.95] hover:shadow-xl cursor-pointer"
            >
              {isSignedIn ? "Dashboard" : "Login"}
            </button>
          </div>
        </div>
        <div
          className="w-nav-overlay"
          data-wf-ignore=""
          id="w-nav-overlay-0"
        ></div>
      </div>

      {/* ───────────── MOBILE NAVBAR (rt-navbar-6) ───────────── */}
      <div ref={menuRef} className="rt-navbar-6">
        <div
          data-animation="default"
          data-collapse="medium"
          data-duration="400"
          data-easing="ease"
          data-easing2="ease"
          role="banner"
          className="rt-navbar-6 w-nav"
        >
          <div className="rt-navbar-wrapper-5">
            {/* Logo */}
            <Link href="/" className="link-block-2 w-inline-block">
              <Image
                src="/Images/logo1.svg"
                alt="Swift MVP"
                width={100}
                height={33}
                className="image-7"
              />
            </Link>

            {/* Inline nav links (desktop center) */}
            <nav role="navigation" className="rt-nav-menus w-nav-menu">
              <div className="div-block-35">
                <Link
                  href="/"
                  className={`nav-link-top-2 font-plus-jakarta-display font-normal w-nav-link${isActive("/") ? " w--current" : ""}`}
                >
                  Websites
                </Link>
                <Link
                  href="/dashboard/packages"
                  className="nav-link-top-2 font-plus-jakarta-display font-normal w-nav-link"
                >
                  MVPs
                </Link>
                <Link
                  href="/#pricing"
                  className="nav-link-top-2 font-plus-jakarta-display font-normal w-nav-link"
                >
                  Branding
                </Link>
                <Link
                  href="/#how-it-works"
                  onClick={handleHowItWorks}
                  className={`nav-link-top-2  font-plus-jakarta-display font-normal w-nav-link${isActive("/") ? " w--current" : ""}`}
                >
                  How it Works
                </Link>
                <button
                  type="button"
                  onClick={handleMyOrdersClick}
                  className={`nav-link-top-2 font-plus-jakarta-display font-normal w-nav-link text-left${isActive("/dashboard") ? " w--current" : ""}`}
                >
                  My Orders
                </button>

                <a
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick();
                  }}
                  href="#"
                  className="button-mobile desktop w-button transition-all duration-300 hover:scale-[1.05] active:scale-[0.95] hover:shadow-xl hover:brightness-110 cursor-pointer"
                >
                  <Image
                    src={
                      isSignedIn
                        ? "/Images/SideDrawer/dashboardactive.svg"
                        : "/Images/NavbarIconButton.svg"
                    }
                    alt=""
                    width={15}
                    height={15}
                  />
                  <span>{isSignedIn ? "Dashboard" : "Login"}</span>
                </a>
              </div>
            </nav>

            {/* Hamburger + Register button */}
            <div
              id="w-node-e856d6fe-0407-112e-c942-b44b070bf0b7-070bf08f"
              className="rt-navbar-conmtact-and-humburgger-2"
            >
              <div
                className={`rt-menu-button w-nav-button${menuOpen ? " w--open" : ""}`}
                onClick={() => setMenuOpen(!menuOpen)}
                role="button"
                tabIndex={0}
                aria-controls="w-nav-overlay-1"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onKeyDown={(e) => e.key === "Enter" && setMenuOpen(!menuOpen)}
                style={{ WebkitUserSelect: "text" }}
              >
                <div className="rt-hamburger-3">
                  <Image
                    src="/Images/hamburger.png"
                    alt="Hamburger Menu"
                    width={30}
                    height={30}
                    className="h-[30px] w-[30px]"
                  />
                </div>
              </div>

              <a
                onClick={(e) => {
                  e.preventDefault();
                  handleClick();
                }}
                href="#"
                className="button-4 desktop w-button transition-all duration-300 hover:scale-[1.05] active:scale-[0.95] hover:shadow-xl hover:brightness-110 cursor-pointer"
              >
                <Image
                  src={
                    isSignedIn
                      ? "/Images/SideDrawer/dashboardactive.svg"
                      : "/Images/NavbarIconButton.svg"
                  }
                  alt=""
                  width={15}
                  height={15}
                />
                <span>{isSignedIn ? "Dashboard" : "Login"}</span>
              </a>
            </div>
          </div>

          {/* Slide-down overlay nav */}
          <div
            className="w-nav-overlay "
            data-wf-ignore=""
            id="w-nav-overlay-1"
            style={{
              display: menuOpen ? "block" : "none",
              overflow: "visible",
            }}
            onClick={close}
          >
            <nav
              role="navigation"
              className="rt-nav-menus w-nav-menu"
              style={{
                display: menuOpen ? "flex" : "none",
                flexDirection: "column",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="div-block-35">
                {/* Close Button Inside Menu */}
                <div className="w-full flex justify-end md:hidden">
                  <button
                    onClick={close}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors absolute right-[5%] top-[5%]"
                    aria-label="Close menu"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-600"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <Link
                  href="/"
                  onClick={close}
                  className={`nav-link-top-2 w-nav-link w--nav-link-open${isActive("/") ? " w--current" : ""}`}
                >
                  Websites
                </Link>
                <Link
                  href="/dashboard/packages"
                  onClick={close}
                  className="nav-link-top-2 w-nav-link w--nav-link-open"
                >
                  MVPs
                </Link>
                <Link
                  href="/#pricing"
                  onClick={close}
                  className="nav-link-top-2 w-nav-link w--nav-link-open"
                >
                  Branding
                </Link>
                <Link
                  href="/#how-it-works"
                  onClick={handleHowItWorks}
                  className={`nav-link-top-2 w-nav-link w--nav-link-open${isActive("/") ? " w--current" : ""}`}
                >
                  How it Works
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    handleMyOrdersClick();
                  }}
                  className={`nav-link-top-2 w-nav-link w--nav-link-open text-left${isActive("/dashboard") ? " w--current" : ""}`}
                >
                  My Orders
                </button>

                <a
                  onClick={(e) => {
                    e.preventDefault();
                    close();
                    handleClick();
                  }}
                  href="#"
                  className="button-mobile desktop w-button transition-all duration-300 hover:scale-[1.05] active:scale-[0.95] hover:shadow-xl hover:brightness-110 cursor-pointer"
                >
                  {isSignedIn ? "Dashboard" : "Login"}
                </a>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
